import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";

import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import { Alert, Box, MenuItem, TextField, Typography, Divider } from "@mui/material";

import * as em from "common/ErrorMessages";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../components/common/custom-component/ToastProvider";

import { fetchPendingTasksRequest } from "../store/slices/workflows/pendingTasksSlice";
import {
	deleteWorkflowProcessThunk,
	unclaimPendingTaskThunk,
	executePendingTaskThunk,
	fetchProcessDocumentsThunk,
	fetchDocumentDownloadThunk,
} from "../store/thunks/workflowThunks";

import SearchFilterBar from "../components/common/SearchFilterBar";
import { useTableColumns } from "../hooks/useTableColumns";
import { useWorkflowAccess } from "../hooks/useWorkflowAccess";
import { createPendingTasksRequest, mapProcessDocumentResponse } from "../dto/workflowDto";
import { pendingTaskReducer, initialState } from "../reducers/pendingTaskReducer";
import { logger } from "helpers/logger";
import { getErrorMessage } from "helpers/errorUtils";
import { toDDMMYYYY } from "helpers/dateUtils";
import { downloadFromUrl } from "helpers/downloadUtils";

// sessionStorage key for the page number to return to after running a task.
const SAVED_PAGE_KEY = "pendingTasksPageIndex";

export default function PendingTaskPage() {
	//#region Hooks / Services
	const { success: ToastSuccess, error: ToastError, info: ToastInfo } = useToast();
	const [t] = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	//#endregion

	// #region State / Store
	// Use reducer for consolidated state management. The init function restores the page saved
	// before navigating away to run a task, then clears it right away so a later normal visit
	// starts at page 1. Page-specific key: other pages save their own.
	const [state, dispatchState] = useReducer(pendingTaskReducer, initialState, (init) => {
		const savedPage = sessionStorage.getItem(SAVED_PAGE_KEY);
		sessionStorage.removeItem(SAVED_PAGE_KEY);
		return savedPage
			? { ...init, table: { ...init.table, currentPage: parseInt(savedPage, 10) || 1 } }
			: init;
	});
	const recordsPerPage = state.table.recordsPerPage || 10;
	const currentPage = state.table.currentPage;

	const { data: PendingTaskData, loading, error } = useSelector((state) => ({
		data: state.pendingTasks?.data?.data ?? state.pendingTasks?.data,
		loading: state.pendingTasks?.loading,
		error: state.pendingTasks?.error,
	}));

	// #endregion

	const displayData = useMemo(() => {
		const payload = PendingTaskData?.data ?? PendingTaskData;
		if (Array.isArray(payload)) return payload;
		if (Array.isArray(payload?.tasks)) return payload.tasks;
		if (Array.isArray(payload?.records)) return payload.records;
		return [];
	}, [PendingTaskData]);

	const totalCount = useMemo(() => {
		const payload = PendingTaskData?.data ?? PendingTaskData;
		if (typeof payload?.totalCount === "number" && payload.totalCount > 0) return payload.totalCount;
		if (typeof payload?.full_count === "number" && payload.full_count > 0) return payload.full_count;
		return displayData.length;
	}, [PendingTaskData, displayData.length]);

	const totalPages = Math.max(1, Math.ceil((totalCount || 0) / recordsPerPage));

	const paginatedData = useMemo(() => {
		const payload = PendingTaskData?.data ?? PendingTaskData;
		// If data is from getPendingTasks (backend pagination with payload.tasks), displayData is already paginated
		if (Array.isArray(payload?.tasks)) {
			return displayData;
		}
		// If data is from getAll (full records array), apply client-side pagination
		const start = (currentPage - 1) * recordsPerPage;
		const end = start + recordsPerPage;
		return displayData.slice(start, end);
	}, [displayData, PendingTaskData, currentPage, recordsPerPage]);
	// #endregion

	// #region Fetch Pending Tasks
	// Fetches from the current page/size/search/sort state. Handlers only change that state; the
	// effect below refetches whenever it changes. The backend takes the assignee from the token
	// and limits rows to workflows allowed in the selected company. Default order: most recently
	// modified first.
	const fetchPendingTasks = useCallback(() => {
		const request = createPendingTasksRequest({
			pageNumber: currentPage,
			recordsByPage: recordsPerPage,
			searchParam: state.table.searchParams.searchTerm,
			startDate: state.table.searchParams.startDate,
			endDate: state.table.searchParams.endDate,
			sortField: state.table.sortField,
			sortDirection: state.table.sortDirection,
		});
		// Search text and label sorts need the translated labels, so they go to the search endpoint.
		const needsLabels = !!request.searchParam || state.table.sortByLabel;

		dispatch(fetchPendingTasksRequest({ request, needsLabels }));
	}, [dispatch, currentPage, recordsPerPage, state.table.searchParams, state.table.sortField, state.table.sortDirection, state.table.sortByLabel]);

	// location.key: refetch when the user navigates to this page again (even from the same URL).
	useEffect(() => {
		fetchPendingTasks();
	}, [fetchPendingTasks, location.key]);

	// Based on whether a response is in the store, not on how many rows it has: an empty list is
	// still a loaded answer ("no tasks"). Never loaded (or cleared by a company switch) -> full
	// loader; loaded before, even with 0 tasks -> keep showing it with a small refresh spinner.
	const hasLoaded = PendingTaskData != null;
	const isFirstLoad = loading && !hasLoaded;
	const isBackgroundRefreshing = loading && hasLoaded;
	// #endregion

	// #region FILTERS & SORT
	const applyFilters = () => {
		dispatchState({
			type: "SET_SEARCH_PARAMS",
			payload: {
				searchTerm: state.table.searchInput?.trim() || "",
				startDate: toDDMMYYYY(state.table.startDateInput),
				endDate: toDDMMYYYY(state.table.endDateInput),
			},
		});
	};

	const clearFilters = () => {
		dispatchState({ type: "CLEAR_FILTERS" });
	};

	// DataTable calls onSort(columnKey) only. Same column flips asc/desc; a new column starts asc.
	// byLabel comes from the column config (sortByLabel), so no column names live in code.
	const handleSort = (column) => {
		if (!column) return;
		const direction = column === state.table.sortField && state.table.sortDirection === "asc" ? "desc" : "asc";
		const byLabel = columns.find((c) => c.key === column)?.sortByLabel === true;
		dispatchState({ type: "SET_SORT", payload: { field: column, direction, byLabel } });
	};

	const handleChangeRecordsPerPage = (e) => {
		dispatchState({ type: "SET_RECORDS_PER_PAGE", payload: Number(e.target.value) || 10 });
	};

	const handlePageChange = (event, value) => {
		dispatchState({ type: "SET_PAGE", payload: value });
	};
	// #endregion

	//#region EXECUTE PROCESS
	const handleExecute = useCallback(
		async (row) => {
			// The backend checks everything (process id, workflow, assignee, forms) with the caller's
			// roles in the selected company, so nothing is checked or sent from here but the process.
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const result = await dispatch(executePendingTaskThunk({ processInstanceId: row.process_instance_id })).unwrap();

				if (result?.data) {
					sessionStorage.setItem(SAVED_PAGE_KEY, currentPage);
					navigate("/formRenderer", {
						state: {
							...result.data,
							title: t("Execute Task"),
						},
					});
				} else {
					ToastError(result?.message || t("Failed to load task forms"));
				}
			} catch (error) {
				logger.error("Error in handleExecute:", error);
				ToastError(getErrorMessage(error, t("Failed to load task forms")));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[navigate, dispatch, t, ToastError, currentPage]
	);
	//#endregion

	//#region UNCLAIM PROCESS
	// The backend checks everything (assignee, claim_task on the workflow in this company, candidates
	// to return the task to), so only the task id is passed.
	const handleUnclaim = useCallback(
		async (taskId) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				await dispatch(unclaimPendingTaskThunk({ taskId })).unwrap();
				ToastSuccess(t("Task unclaimed successfully"));

				// The task now belongs to its candidates, so it leaves this list.
				fetchPendingTasks();
			} catch (error) {
				logger.error("Error unclaiming task:", error);
				ToastError(getErrorMessage(error, t("Failed to unclaim task")));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, t, ToastSuccess, ToastError, fetchPendingTasks]
	);
	//#endregion

	//#region DELETE PROCESS
	const handleDeleteClick = useCallback((row) => {
		dispatchState({ type: "OPEN_DELETE_DIALOG", payload: row });
	}, []);

	const handleCancelDelete = () => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	};

	// Deletes (cancels) the whole process. The backend checks everything (delete_workflow on the
	// workflow in this company, and the workflow's delete policy: by default only the starter, while
	// nobody has acted on it yet), so only the process id is passed.
	const handleDelete = useCallback(
		async (processInstanceId) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				await dispatch(deleteWorkflowProcessThunk({ processInstanceId })).unwrap();
				ToastSuccess(t(em.deleted_successfully));

				// The process's tasks are no longer open, so they leave this list.
				fetchPendingTasks();
			} catch (error) {
				logger.error("Error deleting process:", error);
				ToastError(getErrorMessage(error, t(em.failed_delete_workflow)));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, t, ToastSuccess, ToastError, fetchPendingTasks]
	);

	const handleConfirmDelete = () => {
		if (!state.dialogs.selectedRecord) return;

		handleDelete(state.dialogs.selectedRecord.process_instance_id);
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	};
	//#endregion

	//#region DOWNLOAD PROCESS
	// Asks the backend for a short-lived (60 s) signed link and starts the download right away. The
	// backend checks download_pdf on the document's workflow in this company first.
	const downloadDocument = useCallback(
		async (documentId) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const result = await dispatch(fetchDocumentDownloadThunk({ documentId })).unwrap();
				downloadFromUrl(result?.data?.url);
			} catch (error) {
				logger.error("Error downloading document:", error);
				ToastError(getErrorMessage(error, t("Failed to download the document")));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, t, ToastError]
	);

	// The process's documents so far (e.g. one report per meeting): none ready -> a message; one ->
	// downloaded directly; several -> a popup to pick one.
	const handleDownloadPdf = useCallback(
		async (row) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			let readyDocuments = [];
			try {
				const result = await dispatch(fetchProcessDocumentsThunk({ processInstanceId: row.process_instance_id })).unwrap();
				readyDocuments = (result?.data || [])
					.map(mapProcessDocumentResponse)
					.filter((document) => document.status === "ready");
			} catch (error) {
				logger.error("Error fetching process documents:", error);
				ToastError(getErrorMessage(error, t("Failed to load the documents")));
				return;
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}

			if (readyDocuments.length === 0) {
				ToastInfo(t("No document has been generated for this process yet"));
			} else if (readyDocuments.length === 1) {
				await downloadDocument(readyDocuments[0].documentId);
			} else {
				dispatchState({ type: "OPEN_DOCUMENTS_DIALOG", payload: readyDocuments });
			}
		},
		[dispatch, t, ToastError, ToastInfo, downloadDocument]
	);

	const handleCloseDocumentsDialog = () => {
		dispatchState({ type: "CLOSE_DOCUMENTS_DIALOG" });
	};

	const handleConfirmDocumentsDialog = (data) => {
		dispatchState({ type: "CLOSE_DOCUMENTS_DIALOG" });
		if (data?.documentId) {
			downloadDocument(data.documentId);
		}
	};

	// One radio option per document, newest last (the order they were generated in). The date tells
	// apart documents with the same name (e.g. several "Meeting report").
	const documentFields = useMemo(() => [
		{
			name: "documentId",
			type: "radio",
			row: false,
			required: true,
			options: state.dialogs.documents.map((document) => ({
				value: document.documentId,
				label: document.createdAt
					? `${t(document.displayName)} (${new Date(document.createdAt).toLocaleString()})`
					: t(document.displayName),
			})),
		},
	], [state.dialogs.documents, t]);

	// Stable reference (the dialog resets its form whenever this changes): the newest is preselected.
	const documentDefaults = useMemo(() => ({
		documentId: state.dialogs.documents[state.dialogs.documents.length - 1]?.documentId ?? "",
	}), [state.dialogs.documents]);
	//#endregion

	//#region PENDING TASKS TABLE COLUMNS
	const actions = useMemo(() => ({
		onExecute: handleExecute,
		onDownloadPdf: handleDownloadPdf,
		onDelete: handleDeleteClick,
		onUnclaim: (row) => handleUnclaim(row.task_id),
	}), [handleExecute, handleDownloadPdf, handleDeleteClick, handleUnclaim]);

	// Button rules in tbl_table_column_configs use "hasWorkflowPermission", which calls
	// canUse(permissionCode, row.process_definition_key).
	const { canUse } = useWorkflowAccess();
	const context = useMemo(() => ({ canUse }), [canUse]);

	const { columns, isLoading: columnsLoading } = useTableColumns('PendingTasks', { actions, context });
	//#endregion

	//#region RETURN COMPONENT
	if (error && displayData?.length === 0) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="h3" color="primary">
					{t("Pending Tasks")}
				</Typography>

				{isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("Manage your assigned tasks and track their progress.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Filters */}
			<Box
				sx={{
					my: 2,
					p: 2,
					borderRadius: 2,
					bgcolor: "rgba(29,131,84,0.04)",
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 2,
				}}
			>
				{/* Search */}
				<Box sx={{ flex: 1, minWidth: 260 }}>
					<SearchFilterBar
						/* Backend search & filtering */
						mode="backend"
						/* Search input */
						searchValue={state.table.searchInput}
						onSearchChange={(v) => dispatchState({ type: "SET_SEARCH_INPUT", payload: v })}
						/* Date range filters */
						showDateRange
						startDateValue={state.table.startDateInput}
						endDateValue={state.table.endDateInput}
						onStartDateChange={(v) => dispatchState({ type: "SET_START_DATE_INPUT", payload: v })}
						onEndDateChange={(v) => dispatchState({ type: "SET_END_DATE_INPUT", payload: v })}
						/* Apply / Clear actions */
						showActions
						onApply={applyFilters}
						onClear={clearFilters}
						/* Disable unused UI elements */
						showFilterButton={false}
						showSearchButton={false}
					/>
				</Box>
			</Box>

			{/* First load: header and filters stay; only the results area (total, table, paging) waits. */}
			{isFirstLoad || columnsLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<Loader variant="dots" message="Loading." />
				</Box>
			) : (
			<>
			<Typography variant="body1">
				{t("Total Pending Tasks")}: {totalCount || 0}
			</Typography>

			{!PendingTaskData || paginatedData?.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No Pending Tasks yet" emptyStateSubtitle="Wait for someone to assign a task for you" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer
						data={paginatedData}
						columns={columns}
						onSort={handleSort}
						sortConfig={{ key: state.table.sortField, direction: state.table.sortDirection }}
						showRowNumbers={true}
						maxHeight={600}
						stickyHeader={true}
					/>
				</Box>
			)}

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<TextField
					select
					size="small"
					label={t("Per page")}
					value={recordsPerPage}
					onChange={handleChangeRecordsPerPage}
					sx={{
						minWidth: 120,
						"& .MuiOutlinedInput-root": {
							"& fieldset": { borderColor: "primary.main" },
							"&:hover fieldset": { borderColor: "primary.main" },
							"&.Mui-focused fieldset": { borderColor: "primary.main" },
						},
					}}
				>
					{[5, 10, 15].map((n) => (
						<MenuItem key={n} value={n}>
							{n}
						</MenuItem>
					))}
				</TextField>

				<CustomPagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					color="primary"
					showFirstButton
					showLastButton
					sx={{ mt: 2 }}
				/>
			</Box>
			</>
			)}

			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title={t("Confirm Delete")}
				message={t(em.areyousuredelete)}
				confirmText={t("DELETE")}
				confirmColor="error"
				cancelText={t("Cancel")}
				showMessage={true}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.documentsOpen}
				onClose={handleCloseDocumentsDialog}
				onConfirm={handleConfirmDocumentsDialog}
				title={t("Download Document")}
				message={t("This process has several documents. Choose the one to download.")}
				fields={documentFields}
				defaultValues={documentDefaults}
				confirmText={t("Download")}
				cancelText={t("Cancel")}
				showMessage={true}
			/>

			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}
